<template>
  <div class="result-page" v-loading="loading" element-loading-text="正在加载判卷结果，请稍候...">
    <!-- 顶部导航栏 -->
    <div class="page-header">
      <div class="header-left">
        <el-button type="text" @click="handleExit" class="exit-btn">退出答题</el-button>
      </div>
      <div class="header-right">
        <span class="job-title">{{ jobTitle }}</span>
        <el-checkbox v-model="onlyShowWrong" @change="handleFilterChange" class="filter-checkbox">
          只看错题
        </el-checkbox>
      </div>
    </div>

    <!-- 状态栏 -->
    <div class="status-bar">
      <div class="status-item">
        <i class="el-icon-success status-icon score-icon"></i>
        <span class="status-value">{{ score }}</span>
        <span class="status-label">得分</span>
      </div>
      <div class="status-item">
        <i class="el-icon-error status-icon error-icon"></i>
        <span class="status-value">{{ correctCount }}/{{ totalCount }}</span>
        <span class="status-label">答对题目数</span>
      </div>
      <div class="status-item">
        <i class="el-icon-time status-icon time-icon"></i>
        <span class="status-value">{{ timeSpent }}</span>
        <span class="status-label">用时</span>
      </div>
    </div>

    <!-- 题目列表 -->
    <div class="question-list">
      <div
        v-for="question in filteredQuestionList"
        :key="question.id"
        class="question-item"
      >
        <!-- 题目标题 -->
        <div class="question-header">
          <span class="question-type">{{ getQuestionTypeText(question.type) }}</span>
          <h3 class="question-title">
            {{ question.title }}
            <span class="question-score">（本题得分：{{ question.score || 0 }}分）</span>
          </h3>
        </div>

        <!-- 单选题 -->
        <div v-if="question.type === 2" class="question-options">
          <div
            v-for="(option, optIndex) in getQuestionOptions(question)"
            :key="optIndex"
            :class="[
              'option-item',
              {
                'correct-answer': option.value === question.answer,
                'user-answer': option.value === question.userAnswer,
                'wrong-answer': question.userAnswer && option.value === question.userAnswer && option.value !== question.answer
              }
            ]"
          >
            <span class="option-label">{{ option.label }}：</span>
            <span class="option-content">{{ option.content }}</span>
          </div>
        </div>

        <!-- 多选题 -->
        <div v-else-if="question.type === 3" class="question-options">
          <div
            v-for="(option, optIndex) in getQuestionOptions(question)"
            :key="optIndex"
            :class="[
              'option-item',
              {
                'correct-answer': isCorrectAnswer(question.answer, option.value),
                'user-answer': isUserAnswer(question.userAnswer, option.value),
                'wrong-answer': isUserAnswer(question.userAnswer, option.value) && !isCorrectAnswer(question.answer, option.value)
              }
            ]"
          >
            <span class="option-label">{{ option.label }}：</span>
            <span class="option-content">{{ option.content }}</span>
          </div>
        </div>

        <!-- 判断题 -->
        <div v-else-if="question.type === 4" class="question-options">
          <div
            v-for="(option, optIndex) in getQuestionOptions(question)"
            :key="optIndex"
            :class="[
              'option-item',
              {
                'correct-answer': option.value === question.answer,
                'user-answer': option.value === question.userAnswer,
                'wrong-answer': question.userAnswer && option.value === question.userAnswer && option.value !== question.answer
              }
            ]"
          >
            <span class="option-label">{{ option.label }}：</span>
            <span class="option-content">{{ option.content }}</span>
          </div>
        </div>

        <!-- 简答题 -->
        <div v-else-if="question.type === 1" class="question-answer">
          <div class="answer-text">
            <div class="answer-label">你的答案：</div>
            <div class="answer-content">{{ question.userAnswer || '未作答' }}</div>
          </div>
          <div class="answer-text">
            <div class="answer-label">参考答案：</div>
            <div class="answer-content">{{ question.answer || '暂无参考答案' }}</div>
          </div>
        </div>

        <!-- 答案反馈 -->
        <div class="answer-feedback" v-if="question.type != 1">
          <span class="correct-answer-text">
            正确答案：{{ formatAnswer(question.answer, question.type) }}
          </span>
          <span
            :class="[
              'user-answer-text',
              { 'wrong-answer-text': !isAnswerCorrect(question) }
            ]"
          >
            你的答案：{{ formatAnswer(question.userAnswer, question.type) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'
import { getAccessToken } from '@/utils/auth'

export default {
  name: 'ResultPage',
  data() {
    return {
      loading: true,
      jobTitle: 'UI工程师',
      onlyShowWrong: false,
      score: 0,
      correctCount: 0,
      totalCount: 0,
      timeSpent: '0分钟',
      questionList: []
    }
  },
  computed: {
    filteredQuestionList() {
      if (this.onlyShowWrong) {
        return this.questionList.filter(q => !this.isAnswerCorrect(q))
      }
      return this.questionList
    }
  },
  async mounted() {
    await this.fetchResultData()
  },
  methods: {
    // 退出答题
    handleExit() {
      this.$router.push('/index')
    },
    // 过滤改变
    handleFilterChange() {
      // 过滤逻辑已在computed中处理
    },
    // 获取题型文本
    getQuestionTypeText(type) {
      const typeMap = {
        1: '简答题',
        2: '单选题',
        3: '多选题',
        4: '判断题'
      }
      return typeMap[type] || ''
    },
    // 获取题目选项
    getQuestionOptions(question) {
      // 判断题：固定返回正确/错误两个选项
      if (question.type === 4) {
        return [
          { label: 'A', value: 'A', content: '正确' },
          { label: 'B', value: 'B', content: '错误' }
        ]
      }

      // 选择题（单选和多选）：解析答案格式
      if ((question.type !== 2 && question.type !== 3) || !question.content) {
        return []
      }

      // 解析答案格式：A:内容1,B:内容2,C:内容3,D:内容4（内容中可含逗号，故在「,字母:」处分割）
      const contentStr = question.content
      const options = []
      const parts = contentStr.split(/,(?=[A-Z]:)/)

      parts.forEach(part => {
        const match = part.match(/^([A-Z]):([\s\S]+)$/)
        if (match) {
          options.push({
            label: match[1],
            value: match[1],
            content: match[2].trim()
          })
        }
      })

      return options
    },
    // 判断是否为正确答案（多选题）
    isCorrectAnswer(correctAnswer, optionValue) {
      if (!correctAnswer) return false
      const correctAnswers = correctAnswer.split(',')
      return correctAnswers.includes(optionValue)
    },
    // 判断是否为用户答案（多选题）
    isUserAnswer(userAnswer, optionValue) {
      if (!userAnswer) return false
      if (Array.isArray(userAnswer)) {
        return userAnswer.includes(optionValue)
      }
      const userAnswers = String(userAnswer).split(',')
      return userAnswers.includes(optionValue)
    },
    // 判断答案是否正确
    isAnswerCorrect(question) {
      if (!question.userAnswer) return false

      if (question.type === 3) {
        // 多选题：比较数组（使用slice()创建新数组，避免修改原数组）
        const correctAnswers = (question.answer || '').split(',').sort()
        const userAnswers = Array.isArray(question.userAnswer)
          ? [...question.userAnswer].sort() // 使用展开运算符创建新数组
          : String(question.userAnswer).split(',').sort()
        return JSON.stringify(correctAnswers) === JSON.stringify(userAnswers)
      } else {
        // 单选、判断、简答题：直接比较
        return String(question.answer) === String(question.userAnswer)
      }
    },
    // 格式化答案显示
    formatAnswer(answer, type) {
      if (!answer) return '未作答'
      if (type === 3 && Array.isArray(answer)) {
        return answer.join(',')
      }
      if (type === 3 && typeof answer === 'string' && answer.includes(',')) {
        return answer
      }
      return String(answer)
    },
    // 获取判卷结果数据
    async fetchResultData() {
      try {
        this.loading = true
        
        // 从路由参数获取examId或recordId
        const examId = this.$route.params.examId || this.$route.query.examId
        const recordId = this.$route.params.recordId || this.$route.query.recordId

        if (!examId && !recordId) {
          this.$message.error('缺少考试记录ID')
          this.loading = false
          return
        }

        const token = getAccessToken()
        const headers = {}
        if (token) {
          headers.Authorization = 'Bearer ' + token
        }

        // 调用获取判卷结果的接口，增加超时时间到120秒
        const response = await request({
          url: '/system/exam-rocord/result',
          method: 'GET',
          params: {
            examId: examId,
            recordId: recordId
          },
          headers: headers,
          timeout: 120000 // 120秒超时，适应大数据量
        })

        // 处理返回的数据
        // request 拦截器已经提取了 response.data，所以 response 就是数据对象
        console.log('接口返回数据:', response)
        
        if (response) {
          this.score = response.score || 0
          this.correctCount = response.correctCount || 0
          this.totalCount = response.totalCount || 0
          this.timeSpent = response.timeSpent || '0分钟'
          this.jobTitle = response.jobTitle || 'UI工程师'
          this.questionList = response.questionList || []

          // 处理题目数据格式（使用批量处理优化性能）
          if (this.questionList && this.questionList.length > 0) {
            this.questionList = this.questionList.map(item => {
              let userAnswer = item.userAnswer || ''
              if (item.type === 3 && typeof userAnswer === 'string' && userAnswer.includes(',')) {
                userAnswer = userAnswer.split(',')
              }

              return {
                id: item.id,
                title: item.title,
                content: item.content,
                answer: item.answer,
                userAnswer: userAnswer,
                type: item.type,
                score: item.score || 0
              }
            })
          }
          
          console.log('数据处理完成，题目数量:', this.questionList.length)
          // 数据加载完成后立即关闭loading
          this.loading = false
        } else {
          console.warn('response为空或undefined')
          this.$message.warning('未获取到数据')
          this.loading = false
        }
      } catch (error) {
        console.error('获取判卷结果失败:', error)
        
        // 更详细的错误提示
        let errorMessage = '获取判卷结果失败，请稍后重试'
        if (error.message) {
          if (error.message.includes('timeout')) {
            errorMessage = '请求超时，数据量较大，请稍后重试'
          } else if (error.message.includes('Network Error')) {
            errorMessage = '网络错误，请检查网络连接后重试'
          } else {
            errorMessage = error.message
          }
        }
        
        this.$message.error(errorMessage)
      } finally {
        // 确保loading状态被关闭
        this.loading = false
        console.log('loading已关闭，当前状态:', this.loading)
      }
    }
  }
}
</script>

<style scoped lang="scss" src="./assets/index.scss" />
